import { observable, reaction, runInAction } from "mobx"

import { type GlobalContext, withGlobalLoader } from "@/lib/context.ts"
import { dropSecureStateCookie, FigmaSecureCookie } from "@/lib/cookieUtils.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { openLinkInNewWindow } from "@/lib/std.ts"

import type { FigmaStatus } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IObservableValue } from "mobx"

export interface FigmaIntegrationTabProps {
  context: GlobalContext
  queries: Queries
}

const log = getLogger("FigmaIntegrationTabModel")

export class FigmaIntegrationTabModel extends Model {
  readonly figmaStatus: IObservableValue<FigmaStatus | null> = observable.box(null)
  private figmaConnectionUrl: string | null = null

  constructor(private props: FigmaIntegrationTabProps) {
    super("FigmaIntegrationTabModel")

    this.lifetime.add(reaction(
      () => ({
        figmaConnected: this.figmaStatus.get(),
      }),
      ({ figmaConnected }) => {
        if (figmaConnected?.isConnected === false) {
          runInAction(() => void this.getFigmaConnectUrl())
        }
        if (figmaConnected?.isConnected === true) {
          dropSecureStateCookie(FigmaSecureCookie)
        }
      }
    ), "FigmaIntegrationTabModel:figmaStatus")

    void this.subscribeToFigmaAuthStatus()
  }

  readonly connectToFigma = async () => {
    if (this.figmaConnectionUrl != null) return openLinkInNewWindow(this.figmaConnectionUrl)

    const url = await withGlobalLoader(this.props.context, this.getFigmaConnectUrl())
    url && openLinkInNewWindow(url)
  }

  readonly disconnectFromFigma = async () => {
    void this.props.queries.apiClient.disconnectFigmaAccount()
  }

  private async subscribeToFigmaAuthStatus() {
    try {
      const subscription = await withGlobalLoader(this.props.context, this.props.queries.subscribeToFigmaAuthStatus())
      runInAction(() => this.figmaStatus.set(subscription.initial))

      const channel = subscription.channel
      this.lifetime.add(channel.subscribe(status => {
        runInAction(() => this.figmaStatus.set(status))
      }), "FigmaIntegrationTabModel:subscribeToFigmaAuthStatus")
    } catch (e) {
      log.error("Failed to subscribe to Figma status:", e)
    }
  }

  private async getFigmaConnectUrl() {
    if (this.figmaConnectionUrl != null) {
      return this.figmaConnectionUrl
    }
    try {
      const url = await this.props.queries.apiClient.startFigmaOAuthFlow()
      this.figmaConnectionUrl = url
      return url
    } catch {
      return null
    }
  }
}